import React, { useEffect, useState } from 'react';

import { Field, Form, Formik, useField } from 'formik';
import * as Yup from 'yup';

import SlideOver from '../../procurement/face/SlideOver';

import { formatActivityDate, formatActivityDateTime, ORG_ROLE_ADMIN, ORG_ROLE_USER, STATUS_DEACTIVATED } from './organisationUserFormat';
import { MfaPill, OrgRolePill, StatusPill } from './OrganisationUserPills';
import OrganisationUserProjects from './OrganisationUserProjects';
import OrganisationUserTimeline from './OrganisationUserTimeline';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import TextButton from 'CommonComponents/TextButton';

import { useToast } from 'Hooks/useToast';

const FORM_ID = 'organisation-user-panel-form';

const schema = () =>
    Yup.object({
        first_name: Yup.string().trim().required($t('First name is required')).max(255),
        last_name: Yup.string().trim().required($t('Last name is required')).max(255),
        org_role: Yup.string().oneOf([ORG_ROLE_ADMIN, ORG_ROLE_USER]).required(),
    });

// The design's label column and spacing, with sentence-case labels: the shared
// form label and text-button styles capitalise every word.
const ROWS = 'tw-mb-0 tw-grid tw-grid-cols-[150px_minmax(0,1fr)] tw-items-center tw-gap-x-3 tw-gap-y-2';
const LABEL = 'tw-mb-0 tw-text-0.5sm tw-font-normal tw-normal-case tw-text-charcoal-75';
const LINK = '!tw-normal-case';
// The design's footer buttons are a step taller and lighter than the shared pill.
const FOOTER_BUTTON = '!tw-h-9 !tw-px-[18px] !tw-text-0.5sm !tw-font-medium';

function Row({ label, children }) {
    return (
        <>
            <dt className={LABEL}>{label}</dt>
            <dd className="tw-mb-0 tw-text-0.5sm tw-text-charcoal">{children}</dd>
        </>
    );
}

function FieldRow({ name, label, children }) {
    const [, meta] = useField(name);

    return (
        <>
            <dt className={LABEL}>
                <label htmlFor={name} className="tw-mb-0">
                    {label}
                </label>
            </dt>
            <dd className="tw-mb-0 tw-space-y-1">
                {children}
                {meta.touched && meta.error ? <div className="tw-text-sm tw-text-red-500">{meta.error}</div> : null}
            </dd>
        </>
    );
}

function Muted({ children }) {
    return <span className="tw-text-charcoal-50">{children}</span>;
}

function PanelBody({ user, actions }) {
    const firstLoginEmpty = user.first_login_recorded ? $t('Never') : $t('Not recorded');

    return (
        <div className="tw-space-y-6">
            <dl className={ROWS}>
                {user.can.edit ? (
                    <>
                        {/* A name is the user's own across every organisation: editable here only once they have joined. */}
                        {user.invite_accepted_at ? (
                            <>
                                <FieldRow name="first_name" label={`${$t('First name')} *`}>
                                    <Field id="first_name" name="first_name" type="text" className="text-input" />
                                </FieldRow>
                                <FieldRow name="last_name" label={`${$t('Last name')} *`}>
                                    <Field id="last_name" name="last_name" type="text" className="text-input" />
                                </FieldRow>
                            </>
                        ) : (
                            <>
                                <Row label={$t('First name')}>{user.first_name}</Row>
                                <Row label={$t('Last name')}>{user.last_name}</Row>
                            </>
                        )}
                        <FieldRow name="org_role" label={$t('Organisation role')}>
                            <Field as="select" id="org_role" name="org_role" className="tw-border-0 select-input">
                                <option value={ORG_ROLE_ADMIN}>{$t('Organisation Admin')}</option>
                                <option value={ORG_ROLE_USER}>{$t('Organisation user')}</option>
                            </Field>
                        </FieldRow>
                    </>
                ) : (
                    <Row label={$t('Organisation role')}>
                        <OrgRolePill orgRole={user.org_role} />
                    </Row>
                )}
                <Row label={$t('Status')}>
                    <StatusPill status={user.status} invitedLabel={$t('Invited, not accepted')} />
                </Row>
                <Row label={$t('Invite first sent')}>
                    {user.invite_first_sent_at ? (
                        user.invite_first_sent_by ? (
                            $t('{{date}} by {{name}}', { date: formatActivityDate(user.invite_first_sent_at), name: user.invite_first_sent_by.name })
                        ) : (
                            formatActivityDate(user.invite_first_sent_at)
                        )
                    ) : (
                        <Muted>{$t('Not recorded')}</Muted>
                    )}
                </Row>
                <Row label={$t('Last invite sent')}>
                    {user.invite_last_sent_at ? formatActivityDate(user.invite_last_sent_at) : <Muted>{$t('Not recorded')}</Muted>}
                    {user.can.resend_invite && (
                        <TextButton variant="primary" className={`${LINK} tw-ms-1.5`} onClick={() => actions.resendInvite(user)}>
                            {$t('Resend invite')}
                        </TextButton>
                    )}
                </Row>
                <Row label={$t('Invite accepted')}>
                    {user.invite_accepted_at ? formatActivityDate(user.invite_accepted_at) : <Muted>{$t('Not yet')}</Muted>}
                </Row>
                <Row label={$t('First login')}>{user.first_login_at ? formatActivityDateTime(user.first_login_at) : <Muted>{firstLoginEmpty}</Muted>}</Row>
                <Row label={$t('Last login')}>{user.last_login_at ? formatActivityDateTime(user.last_login_at) : <Muted>{$t('Never')}</Muted>}</Row>
                <Row label={$t('Logins (all time)')}>{user.logins_count}</Row>
                <Row label={$t('Multi-factor authentication')}>
                    <span className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-2">
                        <MfaPill setUp={user.mfa_set_up} />
                        {user.mfa_required && <span className="tw-text-xs tw-text-charcoal-50">{$t('Required for this organisation')}</span>}
                        {user.can.reset_mfa && (
                            <TextButton variant="primary" className={LINK} onClick={() => actions.resetMfa(user)}>
                                {$t('Reset MFA')}
                            </TextButton>
                        )}
                    </span>
                </Row>
            </dl>

            <OrganisationUserProjects projects={user.projects} />

            <OrganisationUserTimeline entries={user.timeline} />
        </div>
    );
}

function PanelFooter({ user, onClose, saving, actions }) {
    return (
        <>
            {/* The design's filled grey Close; no shared variant is a light fill. */}
            <Button className={`${FOOTER_BUTTON} !tw-bg-charcoal-15 !tw-text-charcoal hover:!tw-bg-charcoal-25`} onClick={onClose}>
                {$t('Close')}
            </Button>
            {user?.can.deactivate && (
                <Button variant="outline-danger" className={`${FOOTER_BUTTON} !tw-border-red`} onClick={() => actions.deactivate(user)}>
                    {$t('Deactivate user')}
                </Button>
            )}
            {user?.can.reactivate && user.status === STATUS_DEACTIVATED && (
                <Button variant="outline" className={FOOTER_BUTTON} onClick={() => actions.reactivate(user)}>
                    {$t('Reactivate user')}
                </Button>
            )}
            {user?.can.edit && (
                <Button variant="primary" className={FOOTER_BUTTON} type="submit" form={FORM_ID} loading={saving} disabled={saving}>
                    {$t('Save changes')}
                </Button>
            )}
        </>
    );
}

/**
 * The member's panel. `user` is the latest detail the page holds, so an
 * action taken from the table refreshes it too.
 */
export default function OrganisationUserPanel({ usersUrl, userId, user, onLoaded, onSaved, onClose, actions }) {
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const toast = useToast();

    useEffect(() => {
        if (!userId) {
            return;
        }

        // A slower response for a member opened earlier must not replace this one.
        let current = true;

        setError(null);
        axios
            .get(`${usersUrl}/${userId}`)
            .then(({ data }) => current && onLoaded(data.data))
            .catch((e) => current && setError(e?.response?.data?.message || $t('Something went wrong')));

        return () => {
            current = false;
        };
    }, [usersUrl, userId, onLoaded]);

    const save = (values) => {
        setSaving(true);

        return axios
            .put(`${usersUrl}/${userId}`, values)
            .then(({ data }) => {
                toast.success($t('The user was updated'));
                onSaved(data.data);
            })
            .catch((e) => toast.error(e?.response?.data?.message || $t('Something went wrong')))
            .finally(() => setSaving(false));
    };

    const current = user && user.id === userId ? user : null;

    return (
        <SlideOver
            isOpen={!!userId}
            onClose={onClose}
            title={current?.name ?? ''}
            subtitle={current?.email}
            widthClassName="tw-max-w-[560px]"
            titleClassName="tw-text-xl tw-font-semibold tw-text-blue"
            footer={<PanelFooter user={current} onClose={onClose} saving={saving} actions={actions} />}
        >
            {error && <Alert type="error">{error}</Alert>}
            {!current && !error && <p className="tw-mb-0 tw-text-sm tw-text-charcoal-75">{$t('Loading')}…</p>}
            {current && (
                <Formik
                    enableReinitialize
                    initialValues={{ first_name: current.first_name, last_name: current.last_name, org_role: current.org_role }}
                    validationSchema={schema()}
                    onSubmit={save}
                >
                    <Form id={FORM_ID}>
                        <PanelBody user={current} actions={actions} />
                    </Form>
                </Formik>
            )}
        </SlideOver>
    );
}
